<template>
  <v-container fluid>
    <alert-unsaved-changes v-if="unsavedChanges && edit"></alert-unsaved-changes>
    <v-form ref="locationForm">
      <v-row>
        <v-col cols="12" sm="12" md="6">
          <label>{{ $t('country') }}</label>
          <p class="label-info">
            Country of incorporation or physical location of the Provider.
          </p>
          <v-autocomplete
            v-model="provider.country"
            :items="countries"
            :item-value="'id'"
            :item-text="'description'"
            return-object
            outlined
            disabled
            :placeholder="$t('country')"
            :rules="requiredRule()"
            class="required"
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" sm="12" md="6">
          <label>{{ $t('emirate') }}</label>
          <p class="label-info">
            Emirate of incorporation or physical location of the Provider.
          </p>
          <v-autocomplete
            v-model="provider.emirate"
            :items="emirates"
            :item-value="'id'"
            :item-text="'description'"
            outlined
            :placeholder="$t('emirate')"
            :rules="requiredRule()"
            class="required"
          ></v-autocomplete>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" sm="12" md="6">
          <label>{{ $t('city') }}</label>
          <p class="label-info">City of incorporation or physical location of the Provider</p>
          <v-text-field
            v-model="provider.city"
            :placeholder="$t('city')"
            outlined
            :rules="minMaxLengthRule(1, 50)"
          ></v-text-field>
        </v-col>
      </v-row>

      <!-- Optional -->
      <v-row v-if="edit">
        <v-col cols="12">
          <v-expansion-panels>
            <v-expansion-panel>
              <v-expansion-panel-header>
                {{ $t('optional-info') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content class="mt-5">
                <v-row>
                  <v-col cols="12" xl="6" lg="6" md="6" sm="12">
                    <label>Street Name and Number</label>
                    <p class="label-info fixed-label-height">
                      Street and Number of incorporation or physical location of the Provider.
                    </p>
                    <v-text-field
                      v-model.trim="optionalProvider.street"
                      outlined
                      :placeholder="'Street Name and Number'"
                      clearable
                    >
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" xl="6" lg="6" md="6" sm="12">
                    <label>Coordinates</label>
                    <p class="label-info fixed-label-height">
                      Location Coordinates (Latitude, longitude)
                    </p>
                    <v-text-field
                      v-model.trim="optionalProvider.coordinates"
                      outlined
                      :placeholder="'Coordinates'"
                      clearable
                      :rules="coordinatesRules"
                    >
                    </v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" xl="6" lg="6" md="6" sm="12">
                    <label>P.O. Box</label>
                    <v-text-field
                      v-model.trim="optionalProvider.poBox"
                      outlined
                      :placeholder="'P.O. Box'"
                      clearable
                    >
                    </v-text-field>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <!-- Buttons -->
      <v-row class="my-5" justify="end">
        <v-btn
          v-if="unsavedChanges && edit"
          @click="cancel"
          outlined
          class="black--text font-weight-bold mx-2"
          rounded
          x-large
        >
          {{ $t('cancel') }}
        </v-btn>
        <v-btn
          v-if="unsavedChanges && edit"
          @click="updateProviderLocationInfo"
          depressed
          rounded
          x-large
          color="primary200"
          class="white--text font-weight-bold"
        >
          {{ $t('update') }}
        </v-btn>
        <v-btn
          v-if="!unsavedChanges"
          depressed
          rounded
          x-large
          color="black"
          @click="back"
          outlined
          class="black--text font-weight-bold mx-2"
        >
          <v-icon color="black">$vuetify.icons.back</v-icon>
          {{ $t('back') }}
        </v-btn>
        <v-btn
          v-if="!unsavedChanges"
          v-show="showNextButton"
          depressed
          rounded
          x-large
          color="primary200"
          @click="next"
          class="white--text font-weight-bold"
        >
          {{ $t('next') }}
          <v-icon color="white">$vuetify.icons.next</v-icon>
        </v-btn>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import util from '@/mixins/util.js';
import AlertUnsavedChanges from '@/components/shared/helpers/AlertUnsavedChanges';
import EventBus from '@/helpers/event-bus.js';
import L from 'leaflet';

export default {
  name: 'AddProviderLocationInfo',
  components: { AlertUnsavedChanges },
  mixins: [util],
  data() {
    return {
      provider: {
        country: { id: 'AE', description: 'United Arab Emirates' },
        emirate: null,
        city: '',
      },
      optionalProvider: {
        street: '',
        coordinates: null,
        poBox: '',
      },
      countries: [],
      emirates: [],
      showNextButton: true,
      unsavedChanges: false,
      dataLoaded: false,
      initialProvider: {},
      initialOptionalProvider: {},
      coordinatesRules: [
        v => this.validateCoordinates(v) || 'Insert valid coordinates e.g 24.4374032, 54.611653',
      ],
    };
  },
  props: {
    edit: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    provider: {
      deep: true,
      handler() {
        if (!this.dataLoaded) return;
        this.unsavedChanges =
          this.provider.emirate !== this.initialProvider.emirate ||
          this.provider.city !== this.initialProvider.city;
      },
    },
    optionalProvider: {
      deep: true,
      handler() {
        if (!this.dataLoaded) return;
        this.unsavedChanges =
          this.optionalProvider.street !== this.initialOptionalProvider.street ||
          this.optionalProvider.coordinates !== this.initialOptionalProvider.coordinates ||
          this.optionalProvider.poBox !== this.initialOptionalProvider.poBox;
      },
    },
  },
  mounted() {
    this.axios.get('/api/v1/countries').then(res => (this.countries = res.data.content));

    this.axios.get('/api/v1/emirates').then(res => (this.emirates = res.data.content));

    if (this.edit) {
      this.getProvider();
    }
  },
  methods: {
    back() {
      this.$emit('tab', 3);
    },
    next() {
      const isFormValid = this.$refs.locationForm.validate();
      if (isFormValid) {
        this.$emit('providerLocationInfo', { ...this.provider });
        this.showNextButton = false;
      }
    },
    getProvider() {
      EventBus.$emit('waiting', true);
      const id = this.$route.params.id;

      this.axios
        .get('/api/v1/provider-settings/' + id)
        .then(res => {
          console.log('Get Provider Location Info', res.data);

          this.provider.emirate = res.data.emirate;
          this.provider.city = res.data.city;
          this.optionalProvider.street = res.data.street;
          this.optionalProvider.coordinates = res.data.coordinates;
          this.optionalProvider.poBox = res.data.poBox;

          this.initialProvider = {
            emirate: res.data.emirate,
            city: res.data.city,
          };

          this.initialOptionalProvider = {
            street: res.data.street,
            coordinates: res.data.coordinates,
            poBox: res.data.poBox,
          };

          this.dataLoaded = true;
        })
        .catch(error => {
          console.log({ error });
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
    deleteEmptyProperties(obj) {
      if (!obj || typeof obj !== 'object') {
        return;
      }

      if (Array.isArray(obj)) {
        obj.forEach((item, index) => {
          if (item === null || item === undefined || item === '') {
            obj.splice(index, 1);
          } else if (typeof item === 'object') {
            this.deleteEmptyProperties(item);
            if (Object.keys(item).length === 0) {
              obj.splice(index, 1);
            }
          }
        });
      } else {
        for (const key in obj) {
          if (obj[key] === null || obj[key] === undefined || obj[key] === '') {
            delete obj[key];
          } else if (Array.isArray(obj[key])) {
            this.deleteEmptyProperties(obj[key]);
            if (obj[key].length === 0) {
              delete obj[key];
            }
          } else if (typeof obj[key] === 'object') {
            this.deleteEmptyProperties(obj[key]);
            if (Object.keys(obj[key]).length === 0) {
              delete obj[key];
            }
          }
        }
      }
    },
    cancel() {
      this.getProvider();
    },
    updateProviderLocationInfo() {
      const id = this.$route.params.id;

      const providerUpdateData = {
        id: id,
        country: this.provider.country,
        emirate: this.provider.emirate,
        city: this.provider.city,
        street: this.optionalProvider.street,
        coordinates: this.optionalProvider.coordinates,
        poBox: this.optionalProvider.poBox,
      };

      this.deleteEmptyProperties(providerUpdateData);

      const isFormValid = this.$refs.locationForm.validate();
      if (isFormValid) {
        this.axios
          .post('/api/v1/provider-settings/location-info', providerUpdateData)
          .then(response => {
            console.log('Provider info updated successfully:', response.data);
            EventBus.$emit('snackbar', {
              text: 'Provider Updated Successfully',
              color: 'primary200',
            });
            this.initialProvider = { ...this.provider };
            this.initialOptionalProvider = { ...this.optionalProvider };
            this.unsavedChanges = false;
            this.$emit('updatedChanges', true);
          })
          .catch(error => {
            console.error('Error updating provider info:', error);
          });
      }
    },
    validateCoordinates(v) {
      if (v == null) {
        return true;
      } else {
        const coordinates = v.split(',');
        try {
          let lat = L.latLng(coordinates[0].trim(), coordinates[1].trim());
          return lat != null;
        } catch (e) {
          console.log(e);
          return false;
        }
      }
    },
  },
};
</script>

<style scoped>
.fixed-label-height {
  min-height: 3em;
}
</style>
